import React, { useState, FormEvent } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  getAdditionalUserInfo,
  GoogleAuthProvider,
  OAuthProvider,
  type User,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { motion } from 'framer-motion';
import { toUserFacingMessage } from '@6tool/shared/errors';
import toast from 'react-hot-toast';
import { AiFillApple } from 'react-icons/ai';
import { FcGoogle } from 'react-icons/fc';
import { auth, functions } from '../config/firebase';
import AuthPageLayout from '../components/AuthPageLayout';
import LoadingSpinner from '../components/LoadingSpinner';
import PartnerConnectIntro from '../components/PartnerConnectIntro';
import { useOAuthAuthorizeSessionPublic } from '../hooks/useOAuthAuthorizeSessionPublic';
import { resolveLoginEmail } from '../utils/resolveLoginEmail';
import { HOMEPAGE_URL } from '../config/urls';

type AuthMethod = 'apple' | 'google' | 'email' | null;

/**
 * DEV-597: backend half of the OAuth "new account" contract. For a brand-new
 * Firebase identity, ensure a Data Connect user row + bridge claims exist, then
 * force-refresh the ID token so the claims are present before consent. Failures
 * are non-blocking — the backend reconcile/lazy paths recover.
 */
async function ensureNewUserProvisioned(user: User): Promise<void> {
  try {
    const ensureProvisioned = httpsCallable(functions, 'ensureCurrentUserProvisionedCallableV2');
    await ensureProvisioned({});
    await user.getIdToken(true);
  } catch (err) {
    console.error('[app-oauth] Failed to provision new account:', err);
  }
}

function SpinnerIcon() {
  return (
    <svg
      className='animate-spin -ml-1 mr-3 h-5 w-5'
      xmlns='http://www.w3.org/2000/svg'
      fill='none'
      viewBox='0 0 24 24'
      aria-hidden='true'
    >
      <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4' />
      <path
        className='opacity-75'
        fill='currentColor'
        d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'
      />
    </svg>
  );
}

export default function LoginPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get('session');
  const mode = searchParams.get('mode') || 'signin';
  const isSignUp = mode === 'signup';
  const {
    session: oauthSession,
    loading: sessionLoading,
    error: sessionError,
  } = useOAuthAuthorizeSessionPublic(sessionId);
  const hasPartnerConnect = Boolean(oauthSession?.partner);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [method, setMethod] = useState<AuthMethod>(null);

  const consentPath = sessionId ? `/consent?session=${sessionId}` : '/consent';

  const goToConsent = () => {
    navigate(consentPath);
  };

  const handleOAuthPopup = async (providerType: 'apple' | 'google') => {
    setLoading(true);
    setMethod(providerType);
    try {
      const provider =
        providerType === 'apple'
          ? (() => {
              const p = new OAuthProvider('apple.com');
              p.addScope('email');
              p.addScope('name');
              return p;
            })()
          : (() => {
              const p = new GoogleAuthProvider();
              p.addScope('email');
              p.addScope('profile');
              return p;
            })();
      const cred = await signInWithPopup(auth, provider);
      if (getAdditionalUserInfo(cred)?.isNewUser) {
        await ensureNewUserProvisioned(cred.user);
      }
      goToConsent();
    } catch (err: unknown) {
      const code =
        err && typeof err === 'object' && 'code' in err
          ? String((err as { code: string }).code)
          : '';
      if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
        return;
      }
      toast.error(toUserFacingMessage(err, { action: 'signIn' }));
    } finally {
      setLoading(false);
      setMethod(null);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setMethod('email');

    const trimmedInput = email.trim();
    const { emailToUse } = await resolveLoginEmail(trimmedInput);

    try {
      if (isSignUp) {
        const cred = await createUserWithEmailAndPassword(auth, emailToUse, password);
        await ensureNewUserProvisioned(cred.user);
      } else {
        await signInWithEmailAndPassword(auth, emailToUse, password);
      }
      goToConsent();
    } catch (err: unknown) {
      toast.error(toUserFacingMessage(err, { action: isSignUp ? 'signUp' : 'signIn' }));
    } finally {
      setLoading(false);
      setMethod(null);
    }
  };

  const emailLabel = (() => {
    const trimmed = email.trim();
    if (!trimmed) return 'Email or Username';
    if (trimmed.includes('@') && !trimmed.startsWith('@')) {
      return (
        <span>
          <span>Email</span> <span className='opacity-20'>or Username</span>
        </span>
      );
    }
    if (trimmed.startsWith('@') || !trimmed.includes('@')) {
      return (
        <span>
          <span className='opacity-20'>Email or</span> <span>Username</span>
        </span>
      );
    }
    return 'Email or Username';
  })();

  const oauthButtonClass =
    'group relative w-full flex justify-center items-center py-3 px-4 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-lg text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transform transition-all duration-200 hover:scale-105 active:scale-95 shadow-md hover:shadow-lg';

  const layoutTitle = hasPartnerConnect ? 'Connect your accounts' : 'Welcome to 6Tool';
  const layoutSubtitle = hasPartnerConnect
    ? null
    : isSignUp
    ? 'Create your account to connect a partner app'
    : 'Sign in to your account to continue';

  return (
    <AuthPageLayout
      title={layoutTitle}
      subtitle={layoutSubtitle}
      hideTopLogo={hasPartnerConnect}
      footer={
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.8 }}
          className='text-center'
        >
          <a
            href={HOMEPAGE_URL}
            className='text-xs text-gray-500 hover:text-gray-700 dark:text-gray-500 dark:hover:text-gray-300 transition-colors inline-flex items-center gap-1'
          >
            <svg className='w-3 h-3' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M10 19l-7-7m0 0l7-7m-7 7h18'
              />
            </svg>
            Back to homepage
          </a>
        </motion.div>
      }
    >
      {sessionId && sessionLoading ? <LoadingSpinner /> : null}

      {sessionId && sessionError ? (
        <p className='mb-4 text-center text-sm text-red-600 dark:text-red-400'>{sessionError}</p>
      ) : null}

      {hasPartnerConnect && oauthSession ? (
        <PartnerConnectIntro partner={oauthSession.partner} isSignUp={isSignUp} />
      ) : null}

      <div className='space-y-4 mb-6'>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.3 }}
        >
          <button
            type='button'
            onClick={() => handleOAuthPopup('apple')}
            disabled={loading}
            className={oauthButtonClass}
          >
            {loading && method === 'apple' ? (
              <SpinnerIcon />
            ) : (
              <AiFillApple className='w-5 h-5 mr-2' />
            )}
            Continue with Apple
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <button
            type='button'
            onClick={() => handleOAuthPopup('google')}
            disabled={loading}
            className={oauthButtonClass}
          >
            {loading && method === 'google' ? (
              <SpinnerIcon />
            ) : (
              <FcGoogle className='w-5 h-5 mr-2' />
            )}
            Continue with Google
          </button>
        </motion.div>

        <motion.div
          className='relative'
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.45 }}
        >
          <div className='absolute inset-0 flex items-center'>
            <div className='w-full border-t border-gray-300 dark:border-gray-600' />
          </div>
          <div className='relative flex justify-center text-sm'>
            <span className='px-2 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400'>
              Or continue with email or username
            </span>
          </div>
        </motion.div>
      </div>

      <form className='space-y-6' onSubmit={handleSubmit}>
        <div className='space-y-4'>
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <label
              htmlFor='email-address'
              className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'
            >
              {emailLabel}
            </label>
            <input
              id='email-address'
              name='email'
              type='text'
              autoComplete='username'
              required
              className='appearance-none relative block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 placeholder-gray-400 dark:placeholder-gray-500 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 sm:text-sm'
              placeholder='Enter your email or username'
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <label
              htmlFor='password'
              className='block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2'
            >
              Password
            </label>
            <input
              id='password'
              name='password'
              type='password'
              autoComplete={isSignUp ? 'new-password' : 'current-password'}
              required
              minLength={6}
              className='appearance-none relative block w-full px-4 py-3 border border-gray-300 dark:border-gray-600 placeholder-gray-400 dark:placeholder-gray-500 text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all duration-200 sm:text-sm'
              placeholder='Enter your password'
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.5 }}
        >
          <button
            type='submit'
            disabled={loading}
            className='group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-gradient-to-r from-indigo-600 to-blue-600 hover:from-indigo-700 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transform transition-all duration-200 hover:scale-105 active:scale-95 shadow-lg hover:shadow-xl'
          >
            {loading && method === 'email' ? (
              <span className='flex items-center'>
                <SpinnerIcon />
                {isSignUp ? 'Creating account...' : 'Signing in...'}
              </span>
            ) : (
              <span className='flex items-center'>
                {isSignUp ? 'Create account' : 'Sign in'}
                <svg
                  className='ml-2 -mr-1 w-5 h-5'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M13 7l5 5m0 0l-5 5m5-5H6'
                  />
                </svg>
              </span>
            )}
          </button>
        </motion.div>
      </form>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.7 }}
        className='mt-4 text-center'
      >
        <p className='text-sm text-gray-600 dark:text-gray-400'>
          {isSignUp ? 'Already have an account?' : "Don't have an account?"}{' '}
          <button
            type='button'
            onClick={() => {
              const nextMode = isSignUp ? 'signin' : 'signup';
              const params = new URLSearchParams(searchParams);
              params.set('mode', nextMode);
              navigate(`/login?${params.toString()}`);
            }}
            className='font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400 dark:hover:text-indigo-300 transition-colors'
          >
            {isSignUp ? 'Sign in' : 'Sign up'}
          </button>
        </p>
      </motion.div>
    </AuthPageLayout>
  );
}
